import React, { useMemo } from 'react';
import { useQuery, gql, TypedDocumentNode } from '@apollo/client';
import { useFeatureFlag, formatVertical, titleize } from '@moda/portal-stanchions';
import {
  ProductListPageCategoryQuery,
  ProductListPageCategoryQueryVariables
} from '../../generated/types';
import {
  DEFAULT_SORT_OPTIONS,
  NEW_SORT_OPTIONS,
  SALE_SORT_OPTIONS,
  SortOption
} from '../../constants';
import { usePLPParams } from '../../hooks/usePLPParams';
import { PAGE_FILTER_FRAGMENT } from '../../components/PageFilter';
import { findSectionModules } from '../../lib/findSectionModules';
import { PRODUCT_LIST_PAGE_RESULT_FRAGMENT, ProductListing } from '../../components/ProductListing';
import { ModuleSection } from '../../components/ModuleSection';
import { NotFoundPage } from '../NotFoundPage';
import { LazyLoad, EnableLazyLoad } from '../../components/LazyLoad';
import { PUMO_MODULE_FRAGMENT } from '../../components/PumoModule';
import { FacadePageType } from '../../lib/getRuleContextFromSlug';
import { useRuleVariables } from '../../hooks/useRuleVariables';

export type RuleFeatureFlag = Record<string, boolean>;

export const PRODUCT_LIST_PAGE_CATEGORY_QUERY: TypedDocumentNode<
  ProductListPageCategoryQuery,
  ProductListPageCategoryQueryVariables
> = gql`
  query ProductListPageCategoryQuery(
    $after: String!
    $attributes: [String]
    $availability: [AvailabilityInput]
    $categoryPath: String
    $countryCode: String
    $designers: [String]
    $dynamicReRanking: Boolean
    $filterColors: [String]
    $materials: [String]
    $noSale: Boolean
    $onSale: Boolean
    $priceRange: PriceRange
    $ruleContexts: [String]
    $rules: [Rule]
    $saleTags: [String]
    $scoring: String
    $section: String!
    $sizes: [String]
    $slug: String!
    $versionId: Int
  ) {
    displayPage(slug: $slug, version_id: $versionId) {
      slug
      name
      targetBuckets {
        name
        modules {
          ...PumoModuleFragment
        }
      }
      meta_description
    }

    categoryResult: product_listing(
      input: {
        attributes: $attributes
        availability: $availability
        category_path: $categoryPath
        country_code: $countryCode
        designers: $designers
        dynamic_reranking: $dynamicReRanking
        filter_colors: $filterColors
        materials: $materials
        on_sale: $onSale
        no_sale: $noSale
        price_range: $priceRange
        sale_tags: $saleTags
        sizes: $sizes
        version_id: $versionId
        scoring: $scoring
        rule_contexts: $ruleContexts
        rules: $rules
      }
      slug: $slug
      section: $section
    ) {
      pageName: page_name
      ...ProductListPageResultFragment
      ...PageFilterFragment
    }
  }
  ${PUMO_MODULE_FRAGMENT}
  ${PRODUCT_LIST_PAGE_RESULT_FRAGMENT}
  ${PAGE_FILTER_FRAGMENT}
`;

const SORT_OPTIONS_MAP: { [key: string]: SortOption[] } = {
  new: NEW_SORT_OPTIONS,
  sale: SALE_SORT_OPTIONS,
  shop: DEFAULT_SORT_OPTIONS
};

const MAX_DESIGNER_LIST = 5;

export const ProductCategoryListingPage: React.FC = () => {
  const showSaleInPLPs = useFeatureFlag('show-sale-in-plps', [
    { default: false },
    { if: true, then: true }
  ]);

  const {
    params: { section = 'shop', vertical, subcategory, category },
    queryVariables
  } = usePLPParams();
  const availableSorts = (section && SORT_OPTIONS_MAP[section]) || DEFAULT_SORT_OPTIONS;
  const slug =
    section === 'shop'
      ? queryVariables.categoryPath
      : queryVariables.categoryPath
        ? `${section}/${queryVariables.categoryPath}`
        : section;
  const ruleVariables = useRuleVariables(FacadePageType.Category, slug);
  const {
    data: currentData,
    previousData,
    error,
    loading
  } = useQuery(PRODUCT_LIST_PAGE_CATEGORY_QUERY, {
    variables: {
      ...queryVariables,
      slug: slug,
      section,
      noSale: !showSaleInPLPs && section !== 'sale',
      ...ruleVariables
    }
  });

  const data = currentData ?? previousData;

  const topModules = useMemo(() => findSectionModules(data, 'top'), [data]);
  const headerModules = useMemo(() => findSectionModules(data, 'header'), [data]);
  const bottomModules = useMemo(() => findSectionModules(data, 'bottom'), [data]);

  const verticalText = formatVertical(vertical);
  const firstAttribute = queryVariables.attributes?.[0];
  const categoryText = firstAttribute || subcategory || category;
  const isHomeHomepage = vertical === 'home' && !categoryText;
  const isBeautyHomepage = vertical === 'beauty' && !categoryText;

  const designers = useMemo(() => {
    const availableDesigners = data?.categoryResult?.designers?.map(designer => designer?.name);
    if (!availableDesigners) return null;
    if (availableDesigners.length === 1) return availableDesigners[0];
    if (availableDesigners.length <= MAX_DESIGNER_LIST) {
      return `${availableDesigners.slice(0, -1).join(', ')} and ${availableDesigners.slice(-1)}`;
    }
    return `${availableDesigners
      .slice(0, MAX_DESIGNER_LIST - 1)
      .join(', ')} and ${availableDesigners.slice(MAX_DESIGNER_LIST - 1, MAX_DESIGNER_LIST)}`;
  }, [data?.categoryResult?.designers]);

  const headerText = useMemo(() => {
    switch (section) {
      case 'shop':
        if (isHomeHomepage) return 'Home Décor & Accessories';
        else if (isBeautyHomepage) return 'All Beauty';
        else if (vertical === 'beauty') return categoryText || verticalText;
        else return `${verticalText} Designer ${categoryText}`;
      case 'sale':
        return vertical === 'beauty'
          ? `${categoryText || verticalText} Sale`
          : `${verticalText} Designer ${categoryText || 'Collection'} Sale`;
      case 'new':
        return vertical === 'beauty'
          ? `New ${categoryText || verticalText}`
          : `New ${verticalText} Designer ${categoryText || 'Clothing & Fashion'}`;
      default:
        return 'Designer Collections';
    }
  }, [section, isHomeHomepage, isBeautyHomepage, vertical, categoryText, verticalText]);

  const title = useMemo(() => {
    switch (section) {
      case 'shop':
        if (isHomeHomepage) return 'Luxury Home Décor & Accessories';
        else if (isBeautyHomepage) return 'All Beauty';
        else if (firstAttribute)
          return vertical === 'beauty'
            ? firstAttribute
            : `${verticalText} Designer ${firstAttribute}`;
        else if (subcategory)
          return vertical === 'beauty' ? subcategory : `${verticalText} Designer ${subcategory}`;
        else if (category)
          return vertical === 'beauty'
            ? category
            : `${verticalText} Designer ${category} & Luxury Fashion`;
        else return `${verticalText} Designer Collections & Luxury Fashion`;
      case 'sale':
        return vertical === 'beauty'
          ? `${categoryText || verticalText} Sale`
          : `${verticalText} Designer ${categoryText || 'Collection'} Sale`;
      case 'new':
        return vertical === 'beauty'
          ? `New ${categoryText || verticalText}`
          : `New ${verticalText} Designer ${categoryText || 'Clothing & Fashion'}`;
      default:
        return '';
    }
  }, [
    section,
    isHomeHomepage,
    isBeautyHomepage,
    vertical,
    firstAttribute,
    verticalText,
    subcategory,
    category,
    categoryText
  ]);

  const description = useMemo(() => {
    switch (section) {
      case 'shop':
        if (isHomeHomepage)
          return 'Shop the latest luxury and designer home decor including accessories, dinnerware and bedding, all at Moda Operandi.';
        else if (isBeautyHomepage) return 'Shop the latest beauty products at Moda Operandi.';
        else if (designers)
          return `Shop the latest ${verticalText} ${categoryText} collection at Moda Operandi, featuring luxury fashion designers such as ${designers}.`;
        else
          return `Shop the latest ${verticalText} designer ${categoryText} collection at Moda Operandi.`;
      case 'sale':
        if (designers)
          return `Shop ${verticalText} ${
            categoryText ? categoryText + ' ' : ''
          }sale now at Moda Operandi, featuring luxury fashion designers such as ${designers}.`;
        else
          return `Shop the world's best ${verticalText} designer ${
            categoryText ? categoryText + ' ' : ''
          }sale now at Moda Operandi. Discover the latest fashion trends, most coveted pieces, and more.`;
      case 'new':
        if (designers)
          return `Shop new ${verticalText} ${
            categoryText || 'clothing & fashion'
          } at Moda Operandi, featuring luxury fashion designers such as ${designers}.`;
        else
          return `Shop new ${verticalText} designer ${
            categoryText || 'clothing & fashion'
          } at Moda Operandi. Browse our boutique of expertly curated selection featuring the latest fashion trends.`;
    }
  }, [section, verticalText, categoryText, isHomeHomepage, isBeautyHomepage, designers]);

  if (data && !data.categoryResult) return <NotFoundPage />;
  return (
    <>
      {topModules && <ModuleSection modules={topModules.modules} />}
      {headerModules && <ModuleSection modules={headerModules.modules} />}

      <ProductListing
        title={title}
        description={description}
        headerText={titleize(headerText)}
        queryResult={data?.categoryResult}
        error={error}
        availableSorts={availableSorts}
        loading={loading}
      />

      {bottomModules && (
        <EnableLazyLoad>
          <LazyLoad>
            <ModuleSection modules={bottomModules.modules} />
          </LazyLoad>
        </EnableLazyLoad>
      )}
    </>
  );
};

export default ProductCategoryListingPage;
